/**
 * ===================================================================================================
 * LoginForm.js - 로그인 폼 컴포넌트
 * ===================================================================================================
 *
 * 📍 위치: 프론트엔드 > client/src/components/auth/LoginForm.js
 *
 * 🎯 주요 기능:
 *    - 이메일/비밀번호 로그인
 *    - Google 소셜 로그인
 *    - Firebase 인증 처리
 *    - MongoDB 사용자 생성/조회
 *    - 로그인 성공 시 콜백 호출
 *
 * 🔗 연결된 파일:
 *    - ../../config/firebaseConfig.js - Firebase 설정 (auth, googleProvider)
 *    - ./SocialLoginButtons.js - 소셜 로그인 버튼 컴포넌트
 *    - ../modals/CustomAlertModal.js - 커스텀 알림 모달
 *    - /api/auth/login - 이메일 로그인 API
 *    - /api/auth/google - Google 로그인 API
 *
 * 💡 UI 위치:
 *    - 화면: AuthScreen에서 표시되는 로그인 폼
 *    - 접근: 비로그인 상태에서 기본 표시
 *    - 섹션: 이메일/비밀번호 입력, 로그인 버튼, 회원가입 버튼, Google 로그인 버튼
 *
 * ✏️ 수정 가이드:
 *    - 이 파일을 수정하면: 로그인 폼 UI 및 로그인 처리 로직 변경
 *    - 소셜 로그인 추가: handleGoogleLogin과 유사한 함수 생성 및 SocialLoginButtons에 전달
 *    - 유효성 검증 추가: handleLogin 내부에 검증 로직 추가
 *
 * 📝 참고사항:
 *    - Firebase 인증 후 MongoDB에 사용자 정보 저장/조회
 *    - Enter 키로 로그인 가능 (handleKeyPress)
 *    - Google 로그인 성공 시 localStorage에 'googleConnected' 저장
 *
 * ===================================================================================================
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
   getRedirectResult,
   signInWithCustomToken,
   signInWithEmailAndPassword,
   signInWithPopup,
   signInWithRedirect
} from 'firebase/auth';
import { auth, googleProvider } from '../../config/firebaseConfig';
import { API_BASE_URL } from '../../config/apiConfig';
import {
   shouldFallbackToRedirect,
   shouldUseRedirectForGoogleAuth
} from '../../utils/authBrowser';
import CustomAlertModal from '../modals/CustomAlertModal';
import SocialLoginButtons from './SocialLoginButtons';

const GOOGLE_AUTH_REDIRECT_PENDING_KEY = 'pendingGoogleAuthRedirect';

/**
 * LoginForm - 로그인 폼 컴포넌트
 *
 * @param {Object} props - 컴포넌트 props
 * @param {Function} props.onClose - 폼 닫기 핸들러
 * @param {Function} props.onRegisterClick - 회원가입 버튼 클릭 핸들러
 * @param {Function} props.onLoginSuccess - 로그인 성공 시 콜백 (user, loginType)
 *
 * @returns {JSX.Element} 로그인 폼 UI
 */
const LoginForm = ({ onClose, onRegisterClick, onLoginSuccess }) => {
   const [email, setEmail] = useState(() => localStorage.getItem('rememberedEmail') || '');
   const [password, setPassword] = useState('');
   const [rememberEmail, setRememberEmail] = useState(() => !!localStorage.getItem('rememberedEmail'));

   // 2단계 로그인 흐름
   const [step, setStep] = useState('email'); // 'email' | 'password' | 'forgot' | 'reset'
   const [isCheckingEmail, setIsCheckingEmail] = useState(false);
   const [resetCode, setResetCode] = useState('');
   const [newPassword, setNewPassword] = useState('');
   const [isResetting, setIsResetting] = useState(false);

   // CustomAlert 상태
   const [alertModal, setAlertModal] = useState({
      isOpen: false,
      title: '',
      message: '',
      type: 'info',
      showCancel: false,
      onConfirm: null,
   });

   // 구글 캘린더 동의 후 복귀 처리
   useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const calendarConnected = params.get('calendarConnected');
      const calendarError = params.get('calendarError');

      const resolveCurrentGoogleUser = async fallbackUser => {
         try {
            const currentUser = await new Promise(resolve => {
               if (auth.currentUser) return resolve(auth.currentUser);
               const unsub = auth.onAuthStateChanged(user => { unsub(); resolve(user); });
            });

            if (currentUser) {
               const idToken = await currentUser.getIdToken();
               const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` }
               });
               if (res.ok) {
                  const data = await res.json();
                  onLoginSuccess(data.user, 'google');
                  return;
               }
            }
         } catch (error) {
            console.warn('[LoginForm] Google user refresh failed:', error);
         }

         if (fallbackUser) onLoginSuccess(fallbackUser, 'google');
      };

      if (calendarConnected === 'true') {
         const pendingUser = localStorage.getItem('pendingGoogleUser');
         localStorage.removeItem('pendingGoogleUser');
         window.history.replaceState({}, document.title, window.location.pathname);

         let parsedUser = null;
         if (pendingUser) {
            try {
               parsedUser = JSON.parse(pendingUser);
            } catch (error) {
               console.warn('[LoginForm] Failed to parse pending Google user:', error);
            }
         }
         resolveCurrentGoogleUser(parsedUser);
      } else if (calendarError) {
         const pendingUser = localStorage.getItem('pendingGoogleUser');
         localStorage.removeItem('pendingGoogleUser');
         window.history.replaceState({}, document.title, window.location.pathname);
         if (pendingUser) {
            try {
               onLoginSuccess(JSON.parse(pendingUser), 'google');
            } catch (error) {
               console.warn('[LoginForm] Failed to restore Google user after calendar error:', error);
            }
         }
      }
   }, [onLoginSuccess]);

/**
    * showAlert - 알림 모달 표시 함수
    *
    * @description CustomAlertModal을 통해 알림 메시지를 표시
    * @param {string} message - 표시할 메시지
    * @param {string} type - 알림 타입 (info, success, warning, error)
    * @param {string} title - 알림 제목
    * @param {boolean} showCancel - 취소 버튼 표시 여부
    * @param {Function} onConfirm - 확인 버튼 클릭 시 콜백 함수
    */
   const showAlert = useCallback((message, type = 'info', title = '', showCancel = false, onConfirm = null) => {
      setAlertModal({
         isOpen: true,
         title,
         message,
         type,
         showCancel,
         onConfirm,
      });
   }, []);

   /**
    * closeAlert - 알림 모달 닫기 함수
    *
    * @description 열려있는 알림 모달을 닫음
    */
   const closeAlert = useCallback(() => {
      setAlertModal(prev => ({ ...prev, isOpen: false }));
   }, []);

   const completeGoogleLogin = useCallback(async firebaseUser => {
      const idToken = await firebaseUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` }
      });
      const data = await response.json();
      if (!response.ok) {
         localStorage.setItem('googleConnected', 'false');
         throw new Error(data.msg || 'Google 로그인 실패');
      }

      localStorage.setItem('googleConnected', 'true');
      if (!data.user.google || (!data.user.google.refreshToken && !data.user.google.id)) {
         try {
            const returnUrl = encodeURIComponent('/auth');
            const consentRes = await fetch(`${API_BASE_URL}/api/auth/google/calendar-consent?returnUrl=${returnUrl}`, {
               headers: { 'Authorization': `Bearer ${idToken}` }
            });
            const consentData = await consentRes.json();
            if (consentRes.ok && consentData.url) {
               localStorage.setItem('pendingGoogleUser', JSON.stringify(data.user));
               window.location.href = consentData.url;
               return;
            }
         } catch (consentError) {
            console.warn('[LoginForm] Calendar consent request failed:', consentError);
         }
      }
      onLoginSuccess(data.user, 'google');
   }, [onLoginSuccess]);

   useEffect(() => {
      if (localStorage.getItem(GOOGLE_AUTH_REDIRECT_PENDING_KEY) !== 'true') return undefined;

      let cancelled = false;
      getRedirectResult(auth)
         .then(result => {
            if (cancelled) return;
            localStorage.removeItem(GOOGLE_AUTH_REDIRECT_PENDING_KEY);
            if (result?.user) return completeGoogleLogin(result.user);
         })
         .catch(error => {
            if (cancelled) return;
            localStorage.removeItem(GOOGLE_AUTH_REDIRECT_PENDING_KEY);
            localStorage.setItem('googleConnected', 'false');
            showAlert(`Google 로그인 실패: ${error.message}`, 'error', 'Google 로그인 실패');
         });

      return () => { cancelled = true; };
   }, [completeGoogleLogin, showAlert]);

   // 1단계: 이메일 입력 후 "다음"
   // - 비밀번호 있으면 → 비밀번호 입력 단계
   // - 비밀번호 없으면 → 서버가 customToken 즉시 발급 → 자동 로그인
   const handleCheckEmail = async () => {
      if (!email || !/\S+@\S+\.\S+/.test(email)) {
         showAlert('유효한 이메일을 입력해주세요.', 'warning', '입력 오류');
         return;
      }
      if (rememberEmail) {
         localStorage.setItem('rememberedEmail', email);
      } else {
         localStorage.removeItem('rememberedEmail');
      }
      setIsCheckingEmail(true);
      try {
         const res = await fetch(`${API_BASE_URL}/api/auth/check-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
         });
         const data = await res.json();
         if (!res.ok) {
            showAlert(data.msg || '이메일을 확인할 수 없습니다.', 'error', '오류');
            return;
         }
         if (data.hasPassword) {
            setStep('password');
         } else {
            // 비밀번호 없는 계정 → customToken으로 즉시 로그인
            const credential = await signInWithCustomToken(auth, data.customToken);
            const idToken = await credential.user.getIdToken();
            const loginRes = await fetch(`${API_BASE_URL}/api/auth/login`, {
               method: 'POST',
               headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            });
            const loginData = await loginRes.json();
            if (!loginRes.ok) throw new Error(loginData.msg || '로그인 실패');
            onLoginSuccess(loginData.user, 'general');
         }
      } catch (err) {
         showAlert(err.message || '네트워크 오류가 발생했습니다.', 'error', '오류');
      } finally {
         setIsCheckingEmail(false);
      }
   };

   /**
    * handleLogin - 이메일/비밀번호 로그인 처리
    */
   const handleLogin = async () => {
      if (!email || !password) {
         showAlert('이메일과 비밀번호를 입력해주세요.', 'warning', '입력 오류');
         return;
      }
      if (rememberEmail) {
         localStorage.setItem('rememberedEmail', email);
      } else {
         localStorage.removeItem('rememberedEmail');
      }

      try {
         // Sign in with Firebase
         const userCredential = await signInWithEmailAndPassword(auth, email, password);
         const firebaseUser = userCredential.user;

         // Get ID token
         const idToken = await firebaseUser.getIdToken();

         // Send to backend to get/create user in MongoDB
         const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${idToken}`
            }
         });

         const data = await response.json();

         if (!response.ok) {
            throw new Error(data.msg || '로그인 실패');
         }

         onLoginSuccess(data.user, 'general');
      } catch (error) {
         showAlert('이메일/비밀번호가 일치하지 않습니다.', 'error', '로그인 실패');
      }
   };

   /**
    * handleGoogleLogin - Google 소셜 로그인 처리
    *
    * @description Firebase Google 인증 후 MongoDB에 사용자 정보 저장/조회하여 로그인 처리
    */
   const handleGoogleLogin = async () => {
      try {
         if (shouldUseRedirectForGoogleAuth()) {
            localStorage.setItem(GOOGLE_AUTH_REDIRECT_PENDING_KEY, 'true');
            await signInWithRedirect(auth, googleProvider);
            return;
         }

         const result = await signInWithPopup(auth, googleProvider);
         await completeGoogleLogin(result.user);
      } catch (error) {
         if (shouldFallbackToRedirect(error)) {
            try {
               localStorage.setItem(GOOGLE_AUTH_REDIRECT_PENDING_KEY, 'true');
               await signInWithRedirect(auth, googleProvider);
               return;
            } catch (redirectError) {
               localStorage.removeItem(GOOGLE_AUTH_REDIRECT_PENDING_KEY);
               localStorage.setItem('googleConnected', 'false');
               showAlert(`Google 로그인 실패: ${redirectError.message}`, 'error', 'Google 로그인 실패');
               return;
            }
         }

         localStorage.removeItem(GOOGLE_AUTH_REDIRECT_PENDING_KEY);
         localStorage.setItem('googleConnected', 'false');
         showAlert(`Google 로그인 실패: ${error.message}`, 'error', 'Google 로그인 실패');
      }
   };

   const handleForgotPassword = async () => {
      if (!email || !/\S+@\S+\.\S+/.test(email)) {
         showAlert('이메일을 먼저 입력해주세요.', 'warning', '입력 오류');
         return;
      }
      setIsResetting(true);
      try {
         const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
         });
         const data = await res.json();
         if (!res.ok) { showAlert(data.msg, 'error', '오류'); return; }
         setStep('reset');
      } catch {
         showAlert('네트워크 오류가 발생했습니다.', 'error', '오류');
      } finally {
         setIsResetting(false);
      }
   };

   const handleResetPassword = async () => {
      if (!resetCode || !newPassword) {
         showAlert('인증코드와 새 비밀번호를 입력해주세요.', 'warning', '입력 오류');
         return;
      }
      setIsResetting(true);
      try {
         const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code: resetCode, newPassword }),
         });
         const data = await res.json();
         if (!res.ok) { showAlert(data.msg, 'error', '오류'); return; }
         showAlert('비밀번호가 변경되었습니다. 다시 로그인해주세요.', 'success', '완료');
         setStep('password');
         setResetCode('');
         setNewPassword('');
      } catch {
         showAlert('네트워크 오류가 발생했습니다.', 'error', '오류');
      } finally {
         setIsResetting(false);
      }
   };

   const handleKeyPress = event => {
      if (event.key !== 'Enter') return;
      if (step === 'email') handleCheckEmail();
      else if (step === 'password') handleLogin();
      else if (step === 'reset') handleResetPassword();
   };

   return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
         <div className="bg-white w-11/12 max-w-md rounded-lg shadow-xl p-6">
            <div className="relative mb-4">
               <h2 className="text-xl font-bold text-gray-800 text-center">로그인</h2>
            </div>

            <div className="space-y-4">
               {/* 이메일 필드 (항상 표시) */}
               <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                  <input
                     type="email"
                     className="w-full border border-gray-300 rounded-md px-3 py-2"
                     value={email}
                     onChange={e => { setEmail(e.target.value); setStep('email'); setPassword(''); }}
                     onKeyPress={handleKeyPress}
                  />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer select-none w-fit">
                     <input
                        type="checkbox"
                        checked={rememberEmail}
                        onChange={e => setRememberEmail(e.target.checked)}
                        className="w-4 h-4 accent-blue-500"
                     />
                     <span className="text-sm text-gray-600">아이디 기억하기</span>
                  </label>
               </div>

               {/* 비밀번호 */}
               {step === 'password' && (
                  <div>
                     <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">비밀번호</label>
                        <button
                           type="button"
                           onClick={() => setStep('forgot')}
                           className="text-xs text-blue-500 hover:text-blue-700"
                        >
                           비밀번호 찾기
                        </button>
                     </div>
                     <input
                        type="password"
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                     />
                  </div>
               )}

               {/* 비밀번호 찾기: 코드 발송 안내 */}
               {step === 'forgot' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-700">
                     <p><span className="font-semibold">{email}</span> 으로 인증코드를 발송합니다.</p>
                  </div>
               )}

               {/* 비밀번호 재설정: 코드 + 새 비밀번호 */}
               {step === 'reset' && (
                  <>
                     <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">인증코드</label>
                        <input
                           type="text"
                           className="w-full border border-gray-300 rounded-md px-3 py-2"
                           placeholder="이메일로 받은 2자리 코드"
                           value={resetCode}
                           onChange={e => setResetCode(e.target.value)}
                           onKeyPress={handleKeyPress}
                           autoFocus
                           maxLength={2}
                        />
                     </div>
                     <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호</label>
                        <input
                           type="password"
                           className="w-full border border-gray-300 rounded-md px-3 py-2"
                           placeholder="6자 이상"
                           value={newPassword}
                           onChange={e => setNewPassword(e.target.value)}
                           onKeyPress={handleKeyPress}
                        />
                     </div>
                  </>
               )}

            </div>

            <div className="mt-6 flex flex-col space-y-3">
               {(step === 'email' || step === 'password') && (
                  <button
                     onClick={step === 'email' ? handleCheckEmail : handleLogin}
                     disabled={isCheckingEmail}
                     className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300"
                  >
                     {step === 'email' ? (isCheckingEmail ? '확인 중...' : '다음') : '로그인'}
                  </button>
               )}
               {step === 'forgot' && (
                  <>
                     <button
                        onClick={handleForgotPassword}
                        disabled={isResetting}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300"
                     >
                        {isResetting ? '발송 중...' : '인증코드 발송'}
                     </button>
                     <button onClick={() => setStep('password')} className="px-4 py-2 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 text-sm">
                        취소
                     </button>
                  </>
               )}
               {step === 'reset' && (
                  <>
                     <button
                        onClick={handleResetPassword}
                        disabled={isResetting}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300"
                     >
                        {isResetting ? '변경 중...' : '비밀번호 변경'}
                     </button>
                     <button onClick={() => setStep('forgot')} className="px-4 py-2 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 text-sm">
                        이전
                     </button>
                  </>
               )}

               <button
                  onClick={onRegisterClick}
                  className="px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50">
                  회원가입
               </button>

               <SocialLoginButtons googleLogin={handleGoogleLogin} showAlert={showAlert} />
            </div>

            <CustomAlertModal
               isOpen={alertModal.isOpen}
               onClose={closeAlert}
               onConfirm={alertModal.onConfirm}
               title={alertModal.title}
               message={alertModal.message}
               type={alertModal.type}
               showCancel={alertModal.showCancel}
            />
         </div>
      </div>
   );
};

export default LoginForm;
